import { computed } from 'vue';
import { DEFAULT_FETCH_OPTIONS } from '~/utils/defaultFetchOptions';
import type { ShopAuthStatus } from '~/utils/shop/computeShopAuthStatus';

const DEFAULT_STATUS: ShopAuthStatus = { entra: false, shop: false };
const OIDC_REFRESH_PATH = '/api/_auth/refresh';

/**
 * Unified shop authentication state combining OIDC UI session and Magento token status.
 */
export function useShopAuth() {
  const {
    loggedIn,
    user,
    login,
    logout,
    refresh,
    fetch: fetchSession
  } = useOidcAuth();

  const status = useState<ShopAuthStatus>('shop-auth-status', () => ({
    ...DEFAULT_STATUS
  }));

  const isAuthenticated = computed(() => loggedIn.value && status.value.shop);

  async function syncStatus(): Promise<ShopAuthStatus> {
    const { $locale } = useNuxtApp();

    if ($locale.value === 'zh-cn') {
      status.value = { ...DEFAULT_STATUS };
      return { ...DEFAULT_STATUS };
    }

    try {
      const nextStatus = await $fetch<ShopAuthStatus>('/api/auth/status', {
        ...DEFAULT_FETCH_OPTIONS
      });
      status.value = nextStatus;
      return nextStatus;
    } catch {
      status.value = { ...DEFAULT_STATUS };
      return { ...DEFAULT_STATUS };
    }
  }

  /**
   * Attempts token refresh without triggering OIDC login redirect on failure.
   * Always reconciles local session state via GET /api/_auth/session afterwards.
   */
  async function silentRefresh(): Promise<boolean> {
    const { $locale } = useNuxtApp();

    if ($locale.value !== 'zh-cn') {
      try {
        await $fetch(OIDC_REFRESH_PATH, {
          ...DEFAULT_FETCH_OPTIONS,
          method: 'POST'
        });
      } catch {
        // Refresh may fail when tokens expired — fetch() reconciles server-side state.
      }

      await fetchSession();
    }
    return loggedIn.value;
  }

  return {
    isAuthenticated,
    loggedIn,
    status,
    user,
    login,
    logout,
    refresh,
    silentRefresh,
    syncStatus
  };
}
